import React, {
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import {
  Grid,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  IconButton,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { ArrowBack, Done } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { RFP_ACTION_VALUES, CLIENT_ACTION_VALUES } from "@platform/rfp-core";

import { CoveragesScrollingNav } from "../components";
import { RfpContext } from "../contexts";
import { RFP_ACTION_TYPES } from "../constants";
import { scrollToTop } from "../../../utils";
import RfpRemoveCoverage from "../components/rfpRemoveCoverage";
import { submitEvent } from "../../../utils/tracking";
import { getFilteredCoverages } from "../utils";

import CoverageForm from "./CoverageForms";

const {
  UPDATE_COVERAGE_INDEX,
  UPDATE_SELECTED_COVERAGES,
  UPDATE_BACK_STEP,
  UPDATE_NEXT_STEP,
  HANDLE_NEXT_VALIDATE_COVERAGE,
  HANDLE_BACK_COVERAGE,
} = RFP_ACTION_TYPES;
const { COVERAGE_INDEX, ARE_COVERAGES_VALID, ID } = RFP_ACTION_VALUES;
const { IS_CURRENT_CLIENT, CLIENT_TYPE } = CLIENT_ACTION_VALUES;

const CoverageDetails = ({ px }) => {
  const { rfp, setRfp, handleSaveDraft, form, isSavingDraftRfp } =
    useContext(RfpContext);
  const { coverages, currentCoverages, newCoverages } =
    getFilteredCoverages(rfp);
  const currentCoverageIndex = rfp[COVERAGE_INDEX];
  const currentCoverage = coverages[currentCoverageIndex];
  const [selectedItem, setSelectedItem] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [initialHeight, setInitialHeight] = useState(window.innerHeight - 400);

  // Keep the latest coverages and current index accessible to the mount-only
  // skip effect without making them reactive dependencies
  // (getFilteredCoverages returns a new array on every render).
  const coveragesRef = useRef(coverages);
  coveragesRef.current = coverages;
  const currentCoverageIndexRef = useRef(currentCoverageIndex);
  currentCoverageIndexRef.current = currentCoverageIndex;

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY <= 50) {
        setExpanded(initialHeight + scrollY);
      }
    };

    const handleResize = () => {
      setInitialHeight(window.innerHeight - 400);
      handleScroll();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [initialHeight]);

  // Reset the coverage index when leaving the step.
  useEffect(
    () => () => {
      setRfp({ type: UPDATE_COVERAGE_INDEX, value: 0 });
    },
    [setRfp],
  );

  useLayoutEffect(() => {
    // When entering the Coverage Details step, skip coverages that are
    // already complete (purple checkmark) and land on the first incomplete one.
    // Only auto-skip when the index is still at its default (0) so we don't
    // override intentional navigation to a specific coverage (e.g. the edit
    // action on the Review step).
    if (currentCoverageIndexRef.current !== 0) {
      return;
    }
    const firstIncompleteIndex = coveragesRef.current.findIndex(
      (c) => !c.isValid,
    );
    if (firstIncompleteIndex > 0) {
      setRfp({ type: UPDATE_COVERAGE_INDEX, value: firstIncompleteIndex });
    }
  }, [setRfp]);

  const handleCoverageNext = (coverage) => {
    submitEvent("RFP Next Plan Details Clicked", {
      rfp_id: rfp[ID],
      client_type: form[CLIENT_TYPE],
      coverage_type: coverage?.name,
    });
    const currentIndex = coverages.findIndex((c) => c.id === coverage?.id);
    setRfp({
      type: HANDLE_NEXT_VALIDATE_COVERAGE,
      value: currentCoverage,
      index: currentIndex === -1 ? currentCoverageIndex + 1 : currentIndex,
    });

    if (currentCoverage.isValid) {
      handleSaveDraft();
    }

    scrollToTop();
  };

  const handleCoverageBack = () => {
    setRfp({
      type: HANDLE_BACK_COVERAGE,
    });
    scrollToTop();
  };

  const handleBack = () => {
    setRfp({ type: UPDATE_BACK_STEP });
    scrollToTop();
  };

  const handleSubmit = () => {
    setRfp({ type: UPDATE_NEXT_STEP });
    scrollToTop();
  };

  const handleSubmitDelete = () => {
    const itemIndex = coverages.findIndex((c) => c.id === selectedItem.id);
    let newCurrentCoverageIndex;

    if (itemIndex === 0) {
      newCurrentCoverageIndex = 0;
    } else if (itemIndex <= currentCoverageIndex) {
      newCurrentCoverageIndex = currentCoverageIndex - 1;
    } else {
      newCurrentCoverageIndex = currentCoverageIndex;
    }

    if (coverages.length === 1) {
      handleBack();
    }

    setRfp({ type: UPDATE_COVERAGE_INDEX, value: newCurrentCoverageIndex });
    setRfp({
      type: UPDATE_SELECTED_COVERAGES,
      value: { ...selectedItem, delete: true },
    });

    handleCloseConfirmation();
  };

  const handleCloseConfirmation = () => {
    setSelectedItem(null);
  };

  const isLastCoverage = currentCoverageIndex === coverages.length - 1;
  const isFirstCoverage = currentCoverageIndex === 0;

  return (
    <Grid container justifyContent="center" p={px} size={12}>
      {!!selectedItem && (
        <RfpRemoveCoverage
          isOpen={!!selectedItem}
          onClose={handleCloseConfirmation}
          onCancel={handleCloseConfirmation}
          onSubmit={handleSubmitDelete}
          coverageName={selectedItem?.name}
          rfpId={rfp[ID]}
          clientType={form[CLIENT_TYPE]}
        />
      )}
      <Grid size={12}>
        <Typography variant="display-medium" mb={5} textAlign="center">
          Coverage Details
        </Typography>
      </Grid>
      <Grid container size={3.2} component={Paper}>
        <Grid
          component={CoveragesScrollingNav}
          height={`${expanded || initialHeight}px`}
        >
          <List component="nav" aria-label="selected coverages">
            {!!currentCoverages.length && (
              <CoverageList
                data={currentCoverages}
                title="Current Coverages to Quote"
                handleOnClick={handleCoverageNext}
                currentCoverage={currentCoverage}
                deleteSelection={setSelectedItem}
              />
            )}
            {!!newCoverages.length && (
              <CoverageList
                data={newCoverages}
                title={
                  rfp[IS_CURRENT_CLIENT]
                    ? "New Coverages to Quote"
                    : "Coverages to Quote"
                }
                handleOnClick={handleCoverageNext}
                currentCoverage={currentCoverage}
                deleteSelection={setSelectedItem}
              />
            )}
          </List>
        </Grid>
      </Grid>
      <Grid
        component={Paper}
        size={8.5}
        paddingX={3}
        data-testid="coverage-form"
      >
        <CoverageForm
          coverage={currentCoverage}
          prevCoverage={
            currentCoverageIndex === 0
              ? { name: "" }
              : coverages[currentCoverageIndex - 1]
          }
          nextCoverage={coverages[currentCoverageIndex + 1]}
          isLastCoverage={isLastCoverage}
          isFirstCoverage={isFirstCoverage}
          handleNext={handleCoverageNext}
          handleBack={handleCoverageBack}
        />
      </Grid>
      <Grid container size={12} justifyContent="space-between" mt={3}>
        <Button
          variant="tertiary"
          onClick={handleBack}
          startIcon={<ArrowBack />}
          disabled={isSavingDraftRfp}
        >
          Back
        </Button>
        <Button
          data-testid="coverage-details-next"
          variant="primary"
          disabled={!rfp[ARE_COVERAGES_VALID] || isSavingDraftRfp}
          onClick={handleSubmit}
        >
          Next: Carriers
        </Button>
      </Grid>
    </Grid>
  );
};

const CoverageList = ({
  data,
  title,
  handleOnClick,
  currentCoverage,
  deleteSelection,
}) => (
  <Grid mb={1}>
    <Typography variant="header-large" mb={1}>
      {title}
    </Typography>
    {data.map((coverage) => (
      <ListItem
        key={coverage.name}
        secondaryAction={
          <IconButton size="small" onClick={() => deleteSelection(coverage)}>
            <CloseIcon />
          </IconButton>
        }
        disablePadding
      >
        <ListItemButton
          key={coverage.name}
          selected={currentCoverage?.id === coverage.id}
          onClick={() => {
            handleOnClick(coverage);
          }}
          sx={{ padding: 0 }}
        >
          <ListItemText
            slotProps={{
              primary: {
                paddingRight: 2.5,
              },
            }}
            primary={
              <>
                {coverage.name}{" "}
                {coverage.isValid && (
                  <Done
                    sx={({ palette }) => ({
                      color: palette.rules.done,
                      verticalAlign: "bottom",
                    })}
                  />
                )}
              </>
            }
          />
        </ListItemButton>
      </ListItem>
    ))}
  </Grid>
);

export default CoverageDetails;
